<template>
  <v-dialog
    :value="value"
    persistent
    max-width="1100px"
    content-class="ocpp-emulator-dialog"
    @input="onDialogInput"
  >
    <v-card class="ocpp-emulator-card">
      <v-toolbar dense flat color="primary" dark>
        <v-toolbar-title>
          EV Charger Simulator
          <small class="ml-1">({{ chargePointId || '-' }})</small>
        </v-toolbar-title>
        <v-spacer />
        <v-chip
          small
          :color="statusChipColor"
          text-color="white"
        >
          {{ statusChipLabel }}
        </v-chip>
        <v-btn
          icon
          dark
          :disabled="busy"
          title="Refresh emulator state"
          @click="refreshSnapshot(false)"
        >
          <v-icon small :class="{ 'fa-spin': loadingSnapshot }">fal fa-sync</v-icon>
        </v-btn>
        <v-btn icon dark title="Close simulator" @click="closeDialog">
          <v-icon>fal fa-times</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="ocpp-emulator-content">
        <v-layout row wrap>
          <v-flex xs12 md5 class="pa-2">
            <div class="charger-screen">
              <div class="charger-screen__title">Virtual Charger Screen</div>
              <div class="charger-screen__line">
                <span>Charge Point</span>
                <strong>{{ chargePointId || '-' }}</strong>
              </div>
              <div class="charger-screen__line">
                <span>Connected</span>
                <strong>{{ emulatorState && emulatorState.connected ? 'Yes' : 'No' }}</strong>
              </div>
              <div class="charger-screen__line">
                <span>Offline Mode</span>
                <strong>{{ emulatorState && emulatorState.offlineMode ? 'Yes' : 'No' }}</strong>
              </div>
              <div class="charger-screen__line">
                <span>Autopilot</span>
                <strong>{{ emulatorState && emulatorState.autopilotEnabled ? 'On' : 'Off' }}</strong>
              </div>
              <div class="charger-screen__line">
                <span>Profile</span>
                <strong>{{ activeProfileName }}</strong>
              </div>
              <div class="charger-screen__line">
                <span>Current Energy (Wh)</span>
                <strong>{{ activeEnergyWh }}</strong>
              </div>
              <div class="charger-screen__line">
                <span>Buffered Offline Rows</span>
                <strong>{{ pendingOfflineRows }}</strong>
              </div>
              <div class="charger-screen__line">
                <span>Transaction</span>
                <strong>{{ activeTransactionId }}</strong>
              </div>
              <div class="charger-screen__line">
                <span>Plug Gun</span>
                <strong>{{ helper.gunPlugged ? 'Plugged In' : 'Hoisted' }}</strong>
              </div>
            </div>

            <v-alert
              v-if="lastErrorMessage"
              :value="true"
              type="warning"
              outline
              class="mt-2"
            >
              {{ lastErrorMessage }}
            </v-alert>

            <v-layout row wrap class="mt-2">
              <v-flex xs6 pa-1>
                <v-btn
                  block
                  small
                  color="success"
                  :disabled="busy || isConnected"
                  @click="connectCharger"
                >
                  Connect
                </v-btn>
              </v-flex>
              <v-flex xs6 pa-1>
                <v-btn
                  block
                  small
                  color="warning"
                  :disabled="busy || !isConnected"
                  @click="disconnectCharger"
                >
                  Disconnect
                </v-btn>
              </v-flex>
            </v-layout>

            <v-layout row wrap>
              <v-flex xs6 pa-1>
                <v-btn
                  block
                  small
                  color="info"
                  :disabled="busy || isOffline"
                  @click="goOffline"
                >
                  Go Offline
                </v-btn>
              </v-flex>
              <v-flex xs6 pa-1>
                <v-btn
                  block
                  small
                  color="info"
                  :disabled="busy || !isOffline"
                  @click="goOnline"
                >
                  Go Online
                </v-btn>
              </v-flex>
            </v-layout>
          </v-flex>

          <v-flex xs12 md7 class="pa-2">
            <div class="subheading font-weight-bold mb-2">Control Panel</div>

            <v-layout row wrap>
              <v-flex xs12 sm6 pa-1>
                <v-text-field
                  v-model.trim="form.idTag"
                  label="idTag"
                  hide-details
                  :disabled="busy"
                />
              </v-flex>
              <v-flex xs12 sm6 pa-1>
                <v-text-field
                  v-model.number="form.connectorId"
                  label="Connector ID"
                  type="number"
                  min="1"
                  hide-details
                  :disabled="busy"
                />
              </v-flex>
            </v-layout>

            <v-layout row wrap>
              <v-flex xs12 sm6 pa-1>
                <v-select
                  v-model="form.profile"
                  :items="profileOptions"
                  item-text="text"
                  item-value="value"
                  label="Fake Vehicle / Profile"
                  hide-details
                  :disabled="busy"
                />
              </v-flex>
              <v-flex xs12 sm6 pa-1>
                <v-text-field
                  v-model.number="form.meterIntervalSeconds"
                  label="Meter Interval (sec)"
                  type="number"
                  min="1"
                  hide-details
                  :disabled="busy"
                />
              </v-flex>
            </v-layout>

            <v-layout row wrap>
              <v-flex xs12 sm6 pa-1>
                <v-text-field
                  v-model.number="form.initialEnergyWh"
                  label="Initial Energy (Wh)"
                  type="number"
                  hide-details
                  :disabled="busy"
                />
              </v-flex>
              <v-flex xs12 sm6 pa-1>
                <v-text-field
                  v-model="form.stopReason"
                  label="Stop Reason"
                  hide-details
                  :disabled="busy"
                />
              </v-flex>
            </v-layout>

            <v-layout row wrap>
              <v-flex xs12 pa-1>
                <v-checkbox
                  v-model="form.includeFinalMeterSample"
                  hide-details
                  :disabled="busy"
                  label="Include final meter sample on stop transaction"
                />
              </v-flex>
            </v-layout>

            <v-layout row wrap class="mt-1">
              <v-flex xs12 sm6 pa-1>
                <v-btn
                  block
                  color="success"
                  small
                  :disabled="busy || !canStartCharge"
                  @click="startCharging"
                >
                  Start Charge
                </v-btn>
              </v-flex>
              <v-flex xs12 sm6 pa-1>
                <v-btn
                  block
                  color="warning"
                  small
                  :disabled="busy || !hasActiveTransaction"
                  @click="stopCharging"
                >
                  Stop Charge
                </v-btn>
              </v-flex>
            </v-layout>

            <v-layout row wrap>
              <v-flex xs12 sm6 pa-1>
                <v-btn
                  block
                  color="primary"
                  outline
                  small
                  :disabled="busy || helper.gunPlugged"
                  @click="plugGunIntoVehicle"
                >
                  Plug Gun Into Vehicle
                </v-btn>
              </v-flex>
              <v-flex xs12 sm6 pa-1>
                <v-btn
                  block
                  color="primary"
                  outline
                  small
                  :disabled="busy || !helper.gunPlugged"
                  @click="unplugGunAndHoist"
                >
                  Unplug / Hoist Gun
                </v-btn>
              </v-flex>
            </v-layout>

            <v-layout row wrap>
              <v-flex xs12 sm6 pa-1>
                <v-btn
                  block
                  color="info"
                  small
                  :disabled="busy || !hasActiveTransaction"
                  @click="sendMeterSample"
                >
                  Send One Meter Sample
                </v-btn>
              </v-flex>
              <v-flex xs12 sm6 pa-1>
                <v-btn
                  block
                  small
                  :color="autopilotButtonColor"
                  :disabled="busy || !hasActiveTransaction"
                  @click="toggleAutopilot"
                >
                  {{ autopilotButtonLabel }}
                </v-btn>
              </v-flex>
            </v-layout>

            <v-divider class="my-2" />

            <div class="subheading font-weight-bold mb-1">Received Server Commands</div>
            <div class="received-commands">
              <div v-if="!receivedCommandsPreview.length" class="grey--text text--darken-1 pa-2">
                No commands received yet.
              </div>
              <v-list v-else dense two-line>
                <v-list-tile
                  v-for="(command, index) in receivedCommandsPreview"
                  :key="(command.at || 'unknown') + '-' + index"
                >
                  <v-list-tile-content>
                    <v-list-tile-title>{{ command.action || '-' }}</v-list-tile-title>
                    <v-list-tile-sub-title>{{ formatCommandParams(command) }}</v-list-tile-sub-title>
                  </v-list-tile-content>
                  <v-list-tile-action-text>{{ formatTimestamp(command.at) }}</v-list-tile-action-text>
                </v-list-tile>
              </v-list>
            </div>
          </v-flex>
        </v-layout>
      </v-card-text>

      <v-divider />
      <v-card-actions>
        <small class="grey--text text--darken-1">
          Polling every 5s while open.
        </small>
        <v-spacer />
        <v-btn flat color="warning" :disabled="busy" @click="closeDialog">
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { addAlert } from '@/utilities/helpers'
import {
  getEmulatorCharger,
  connectEmulatorCharger,
  disconnectEmulatorCharger,
  setEmulatorOffline,
  setEmulatorOnline,
  startEmulatorTransaction,
  sendEmulatorMeterValues,
  setEmulatorAutopilot,
  stopEmulatorTransaction,
  getEmulatorReceivedCommands
} from '@/services/ocpp-charger-service'

const POLL_INTERVAL_MS = 5000

export default {
  name: 'ocpp-emulator-dialog',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    chargePointId: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      loadingSnapshot: false,
      actionInProgress: false,
      emulatorState: null,
      receivedCommands: [],
      pollTimerHandle: null,
      lastErrorMessage: '',
      helper: {
        gunPlugged: false
      },
      form: {
        idTag: 'EMU-FLEET-IDTAG-1',
        connectorId: 1,
        profile: 'fleet_bus',
        meterIntervalSeconds: 15,
        initialEnergyWh: null,
        stopReason: 'Local',
        includeFinalMeterSample: true
      }
    }
  },
  computed: {
    profileOptions () {
      return [
        { text: 'Fleet Bus', value: 'fleet_bus' },
        { text: 'Private Vehicle', value: 'private_vehicle' },
        { text: 'Provisioning', value: 'provisioning' }
      ]
    },
    busy () {
      return this.loadingSnapshot || this.actionInProgress
    },
    isConnected () {
      return !!(this.emulatorState && this.emulatorState.connected)
    },
    isOffline () {
      return !!(this.emulatorState && this.emulatorState.offlineMode)
    },
    hasActiveTransaction () {
      return !!(this.emulatorState && this.emulatorState.activeTransaction)
    },
    activeProfileName () {
      if (this.emulatorState && this.emulatorState.activeTransaction && this.emulatorState.activeTransaction.profileName) {
        return this.emulatorState.activeTransaction.profileName
      }
      return this.form.profile || '-'
    },
    activeEnergyWh () {
      if (this.emulatorState && this.emulatorState.activeTransaction && typeof this.emulatorState.activeTransaction.currentEnergyWh !== 'undefined') {
        return this.emulatorState.activeTransaction.currentEnergyWh
      }
      return '-'
    },
    activeTransactionId () {
      if (this.emulatorState && this.emulatorState.activeTransaction && this.emulatorState.activeTransaction.transactionId) {
        return this.emulatorState.activeTransaction.transactionId
      }
      return '-'
    },
    pendingOfflineRows () {
      if (this.emulatorState && typeof this.emulatorState.pendingOfflineMeterRows !== 'undefined') {
        return this.emulatorState.pendingOfflineMeterRows
      }
      return 0
    },
    statusChipColor () {
      if (!this.emulatorState) {
        return 'grey'
      }
      if (this.isOffline) {
        return 'orange'
      }
      if (this.isConnected) {
        return 'green'
      }
      return 'red'
    },
    statusChipLabel () {
      if (!this.emulatorState) {
        return 'Unknown'
      }
      if (this.isOffline) {
        return 'Offline mode'
      }
      return this.isConnected ? 'Connected' : 'Disconnected'
    },
    canStartCharge () {
      return this.isConnected && !this.isOffline && this.helper.gunPlugged && !this.hasActiveTransaction
    },
    autopilotButtonLabel () {
      return this.emulatorState && this.emulatorState.autopilotEnabled ? 'Stop Autopilot' : 'Start Autopilot'
    },
    autopilotButtonColor () {
      return this.emulatorState && this.emulatorState.autopilotEnabled ? 'warning' : 'primary'
    },
    receivedCommandsPreview () {
      return (this.receivedCommands || []).slice(0, 25)
    }
  },
  watch: {
    value (isOpen) {
      if (isOpen) {
        this.onDialogOpened()
      } else {
        this.onDialogClosed()
      }
    },
    chargePointId (nextChargePointId, prevChargePointId) {
      if (nextChargePointId === prevChargePointId) {
        return
      }
      this.emulatorState = null
      this.receivedCommands = []
      if (this.value) {
        this.refreshSnapshot(false)
      }
    }
  },
  mounted () {
    if (this.value) {
      this.onDialogOpened()
    }
  },
  beforeDestroy () {
    this.stopPolling()
  },
  methods: {
    onDialogInput (nextValue) {
      this.$emit('input', !!nextValue)
    },
    closeDialog () {
      this.$emit('input', false)
    },
    onDialogOpened () {
      this.lastErrorMessage = ''
      this.refreshSnapshot(false)
      this.startPolling()
    },
    onDialogClosed () {
      this.stopPolling()
      this.lastErrorMessage = ''
    },
    startPolling () {
      this.stopPolling()
      this.pollTimerHandle = setInterval(() => {
        this.refreshSnapshot(true)
      }, POLL_INTERVAL_MS)
    },
    stopPolling () {
      if (this.pollTimerHandle) {
        clearInterval(this.pollTimerHandle)
        this.pollTimerHandle = null
      }
    },
    sanitizeInt (value, fallback) {
      const parsed = parseInt(value, 10)
      if (!isNaN(parsed)) {
        return parsed
      }
      return fallback
    },
    sanitizeOptionalNumber (value) {
      if (value === null || typeof value === 'undefined' || value === '') {
        return undefined
      }
      const parsed = Number(value)
      if (isNaN(parsed)) {
        return undefined
      }
      return parsed
    },
    syncFormWithState () {
      if (!this.emulatorState) {
        return
      }

      if (this.emulatorState.activeTransaction) {
        this.helper.gunPlugged = true
        if (this.emulatorState.activeTransaction.idTag) {
          this.form.idTag = this.emulatorState.activeTransaction.idTag
        }
        if (this.emulatorState.activeTransaction.connectorId) {
          this.form.connectorId = this.emulatorState.activeTransaction.connectorId
        }
        if (this.emulatorState.activeTransaction.profileName) {
          this.form.profile = this.emulatorState.activeTransaction.profileName
        }
      }

      if (this.emulatorState.autopilotIntervalSeconds) {
        this.form.meterIntervalSeconds = this.emulatorState.autopilotIntervalSeconds
      }
    },
    async refreshSnapshot (silent) {
      if (!this.chargePointId) {
        return
      }
      if (silent && this.loadingSnapshot) {
        return
      }

      this.loadingSnapshot = true
      try {
        const [state, commands] = await Promise.all([
          getEmulatorCharger({ chargePointId: this.chargePointId }),
          getEmulatorReceivedCommands({ chargePointId: this.chargePointId })
        ])
        this.emulatorState = state || null
        this.receivedCommands = Array.isArray(commands) ? commands : []
        this.syncFormWithState()
        this.lastErrorMessage = ''
      } catch (err) {
        this.lastErrorMessage = err && err.message ? err.message : 'Unable to load emulator state'
        if (!silent) {
          addAlert({
            message: this.lastErrorMessage,
            type: 'error',
            isModal: true
          })
        }
      } finally {
        this.loadingSnapshot = false
      }
    },
    async runAction (action, successMessage) {
      if (!this.chargePointId) {
        addAlert({
          message: 'Charge point ID is required to control simulator',
          type: 'warning',
          isModal: true
        })
        return
      }

      this.actionInProgress = true
      try {
        await action()
        if (successMessage) {
          addAlert({ message: successMessage, type: 'success', isModal: true })
        }
        await this.refreshSnapshot(true)
      } catch (err) {
        this.lastErrorMessage = err && err.message ? err.message : 'Emulator action failed'
        addAlert({
          message: this.lastErrorMessage,
          type: 'error',
          isModal: true
        })
      } finally {
        this.actionInProgress = false
      }
    },
    async connectCharger () {
      await this.runAction(() => {
        return connectEmulatorCharger({ chargePointId: this.chargePointId })
      }, 'Emulator connected')
    },
    async disconnectCharger () {
      await this.runAction(() => {
        return disconnectEmulatorCharger({ chargePointId: this.chargePointId })
      }, 'Emulator disconnected')
    },
    async goOffline () {
      await this.runAction(() => {
        return setEmulatorOffline({ chargePointId: this.chargePointId })
      }, 'Emulator moved offline')
    },
    async goOnline () {
      await this.runAction(() => {
        return setEmulatorOnline({ chargePointId: this.chargePointId })
      }, 'Emulator moved online and backfilled')
    },
    async startCharging () {
      if (!this.helper.gunPlugged) {
        addAlert({
          message: 'Plug gun into vehicle before starting charge',
          type: 'warning'
        })
        return
      }
      await this.runAction(async () => {
        await startEmulatorTransaction({
          chargePointId: this.chargePointId,
          connectorId: this.sanitizeInt(this.form.connectorId, 1),
          idTag: this.form.idTag || undefined,
          profile: this.form.profile || 'fleet_bus',
          initialEnergyWh: this.sanitizeOptionalNumber(this.form.initialEnergyWh)
        })
        await setEmulatorAutopilot({
          chargePointId: this.chargePointId,
          on: true,
          profile: this.form.profile || 'fleet_bus',
          intervalSeconds: this.sanitizeInt(this.form.meterIntervalSeconds, 15)
        })
      }, 'Transaction started (autopilot sending meter values)')
    },
    async stopCharging () {
      await this.runAction(() => {
        return stopEmulatorTransaction({
          chargePointId: this.chargePointId,
          reason: this.form.stopReason || 'Local',
          includeFinalMeterSample: !!this.form.includeFinalMeterSample,
          intervalSeconds: this.sanitizeInt(this.form.meterIntervalSeconds, 15)
        })
      }, 'Transaction stopped')
    },
    async sendMeterSample () {
      await this.runAction(() => {
        return sendEmulatorMeterValues({
          chargePointId: this.chargePointId,
          intervalSeconds: this.sanitizeInt(this.form.meterIntervalSeconds, 15)
        })
      }, 'Meter sample sent')
    },
    async toggleAutopilot () {
      const shouldEnable = !(this.emulatorState && this.emulatorState.autopilotEnabled)
      await this.runAction(() => {
        return setEmulatorAutopilot({
          chargePointId: this.chargePointId,
          on: shouldEnable,
          profile: this.form.profile || 'fleet_bus',
          intervalSeconds: this.sanitizeInt(this.form.meterIntervalSeconds, 15)
        })
      }, shouldEnable ? 'Autopilot started' : 'Autopilot stopped')
    },
    plugGunIntoVehicle () {
      this.helper.gunPlugged = true
      addAlert({
        message: 'Gun plugged into vehicle',
        type: 'info',
        isModal: true
      })
    },
    async unplugGunAndHoist () {
      if (this.hasActiveTransaction) {
        await this.runAction(() => {
          return stopEmulatorTransaction({
            chargePointId: this.chargePointId,
            reason: 'EVDisconnected',
            includeFinalMeterSample: !!this.form.includeFinalMeterSample,
            intervalSeconds: this.sanitizeInt(this.form.meterIntervalSeconds, 15)
          })
        }, 'Transaction stopped (EV disconnected)')
      }
      this.helper.gunPlugged = false
      addAlert({
        message: 'Gun unplugged and hoisted',
        type: 'info'
      })
    },
    formatTimestamp (value) {
      if (!value) {
        return '-'
      }
      const date = new Date(value)
      if (isNaN(date.getTime())) {
        return String(value)
      }
      return date.toLocaleString()
    },
    formatCommandParams (command) {
      if (!command || !command.params || !Object.keys(command.params).length) {
        return 'No params'
      }
      let paramsText = ''
      try {
        paramsText = JSON.stringify(command.params)
      } catch (err) {
        paramsText = String(command.params)
      }
      return paramsText.length > 120
        ? paramsText.substring(0, 117) + '...'
        : paramsText
    }
  }
}
</script>

<style scoped>
.ocpp-emulator-card {
  display: flex;
  flex-direction: column;
  max-height: 92vh;
}

.ocpp-emulator-content {
  overflow-y: auto;
}

.charger-screen {
  border-radius: 8px;
  background: #0f1117;
  color: #9fe870;
  border: 1px solid #2b2f3a;
  padding: 12px;
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
}

.charger-screen__title {
  color: #ffffff;
  font-size: 13px;
  letter-spacing: 0.4px;
  margin-bottom: 8px;
}

.charger-screen__line {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  padding: 3px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  font-size: 12px;
}

.charger-screen__line:last-child {
  border-bottom: none;
}

.received-commands {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  max-height: 250px;
  overflow-y: auto;
}
</style>
